import React, { useMemo, useState } from "react";
import { Alert, Button, Col, Dropdown, Form, InputGroup, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCog, faEraser, faMagnifyingGlass, faPlus } from "@fortawesome/free-solid-svg-icons";
import Datetime from "react-datetime";
import { format, isValid, parse } from "date-fns";
import { zonedTimeToUtc } from "date-fns-tz";
import { ColumnSortingTable } from "./Table.js";
import { getDefaultWhatsAppReportDates, whatsAppReportTimestamp } from "../utils/whatsappReportPresentation.js";

const dateBound = (value, timeZone) => {
  if (!value) return null;
  const date = parse(value, "dd/MM/yyyy HH:mm", new Date());
  if (!isValid(date) || format(date, "dd/MM/yyyy HH:mm") !== value) return NaN;
  return zonedTimeToUtc(format(date, "yyyy-MM-dd'T'HH:mm:ss"), timeZone).getTime();
};

export default function WhatsAppReportTable({
  title, description, filename, data, columns, extraColumns = [], eventTimezone,
  dateAccessor, sortAccessor = dateAccessor, statusOptions, statusAccessor = "status", loading = false, error,
  onCreate, createLabel, createDisabled = false, exportNote, children,
}) {
  const [triggerExport, setTriggerExport] = useState(false);
  const [showExtra, setShowExtra] = useState(false);
  const [status, setStatus] = useState("");
  const [dateFilters, setDateFilters] = useState(() => dateAccessor ? getDefaultWhatsAppReportDates() : { startDate: "", endDate: "" });
  const [draftDates, setDraftDates] = useState(dateFilters);
  const [resetTrigger, setResetTrigger] = useState(0);
  const [tableSummary, setTableSummary] = useState({ count: 0, statuses: "" });
  const start = dateBound(dateFilters.startDate, eventTimezone);
  const end = dateBound(dateFilters.endDate, eventTimezone);
  const invalidRange = Number.isNaN(start) || Number.isNaN(end) || (start !== null && end !== null && start > end);
  const pendingDates = draftDates.startDate !== dateFilters.startDate || draftDates.endDate !== dateFilters.endDate;
  const filteredData = useMemo(() => data.filter((row) => {
    if (invalidRange || (status && String(row[statusAccessor]) !== status)) return false;
    if (!dateAccessor || (start === null && end === null)) return true;
    const timestamp = whatsAppReportTimestamp(row[dateAccessor]);
    return Number.isFinite(timestamp) && (start === null || timestamp >= start) && (end === null || timestamp <= end + 59999);
  }), [data, dateAccessor, end, invalidRange, start, status, statusAccessor]);
  const visibleColumns = useMemo(() => showExtra
    ? [...columns.filter((column) => column.id !== "icons"), ...extraColumns, ...columns.filter((column) => column.id === "icons")]
    : columns, [columns, extraColumns, showExtra]);
  const reset = () => {
    const dates = dateAccessor ? getDefaultWhatsAppReportDates() : { startDate: "", endDate: "" };
    setStatus(""); setShowExtra(false); setDraftDates(dates); setDateFilters(dates);
    setResetTrigger((value) => value + 1);
  };
  const applyDateFilters = () => setDateFilters({ ...draftDates });
  const exportMetadata = [
    { label: "Período", value: dateAccessor ? `${dateFilters.startDate || "Sem início"} - ${dateFilters.endDate || "Sem fim"}` : "Sem filtro de data" },
    { label: "Registros", value: String(tableSummary.count) },
    { label: "Status incluídos", value: tableSummary.statuses || "Nenhum registro" },
    { label: "Colunas", value: showExtra ? "Com informações extras" : "Padrão" },
    { label: "Escopo", value: "Todos os resultados dos filtros de data, status e colunas, em todas as páginas" },
    ...(exportNote ? [{ label: "Observação", value: exportNote }] : []),
  ];
  return <section className="mb-4" aria-label={title}>
    <Form.Group as={Col} className="textHeaderAplicativo d-flex flex-wrap justify-content-between align-items-center gap-2">
      <h5 className="card-title mb-0">{title} ({data.length})</h5>
      <div>
        <Dropdown className="d-inline">
          <Dropdown.Toggle className="btn btn-outline-primary" aria-label={`Opções de ${title}`}><FontAwesomeIcon icon={faCog} /></Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item disabled={loading || !!error || invalidRange || pendingDates} onClick={() => setTriggerExport(true)}>Exportar</Dropdown.Item>
            {extraColumns.length > 0 && <Dropdown.Item onClick={() => setShowExtra((value) => !value)}>{showExtra ? "Esconder informações extras" : "Mostrar mais informações"}</Dropdown.Item>}
          </Dropdown.Menu>
        </Dropdown>
        {onCreate && <Button type="button" className="btn btn-outline-primary ms-2" aria-label={createLabel} title={createLabel} disabled={createDisabled} onClick={onCreate}><FontAwesomeIcon icon={faPlus} /></Button>}
      </div>
    </Form.Group>
    <h6 className="text-muted mt-3 mx-2">{description}</h6>
    <Row className="mb-3 mx-0 g-2">
      {dateAccessor && [["startDate", "Data Inicial"], ["endDate", "Data Final"]].map(([key, label]) => <Col key={key} xs={12} sm={6} md={3}>
        <InputGroup className="w-100 flex-nowrap">
          <div className="flex-fill">
            <Datetime className="w-100" value={draftDates[key]} onChange={(date) => setDraftDates((current) => ({ ...current, [key]: typeof date === "string" ? date : date?.isValid?.() ? date.format("DD/MM/YYYY HH:mm") : "" }))}
              dateFormat="DD/MM/YYYY" timeFormat="HH:mm" locale="pt-br"
              inputProps={{ placeholder: label, "aria-label": `${title}: ${label}`, className: "form-control", style: { borderTopRightRadius: 0, borderBottomRightRadius: 0 }, onKeyDown: (event) => { if (event.key === "Enter") { event.preventDefault(); applyDateFilters(); } } }} />
          </div>
          <InputGroup.Text as="button" type="button" className="cursor-pointer" onClick={applyDateFilters} aria-label={`Aplicar filtro de ${label.toLowerCase()}`}>
            <FontAwesomeIcon icon={faMagnifyingGlass} />
          </InputGroup.Text>
        </InputGroup>
      </Col>)}
      {statusOptions && <Col xs={12} sm={6} md={3}><Form.Select className="form-control w-100" aria-label={`Filtrar status de ${title}`} value={status} onChange={(event) => setStatus(event.target.value)}>
        <option value="">Todos os status</option>{Object.entries(statusOptions).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
      </Form.Select></Col>}
      <Col xs={12} sm={6} md={3} className="d-flex align-items-center my-3 mb-sm-0 mt-sm-auto">
        <button type="button" className="btn btn-primary w-100 w-sm-auto" title="Limpar filtros" aria-label={`Limpar filtros de ${title}`} onClick={reset}><FontAwesomeIcon icon={faEraser} /></button>
      </Col>
    </Row>
    {pendingDates && <div className="small text-muted mx-2 mb-3">Aplique as datas na lupa para filtrar e exportar o relatório.</div>}
    {invalidRange && <Alert variant="warning" className="p-2">Informe datas válidas no formato dd/mm/aaaa hh:mm, com a data inicial anterior à final.</Alert>}
    {error && <Alert variant="danger" className="p-2">{error}</Alert>}
    {children}
    <div className="pb-3"><ColumnSortingTable title={filename} columns={visibleColumns} data={error ? [] : filteredData}
      initialState={{ pageIndex: 0, ...(sortAccessor ? { sortBy: [{ id: sortAccessor, desc: true }] } : {}) }}
      exportMetadata={exportMetadata}
      onFilteredDataChange={(rows) => setTableSummary({ count: rows.length, statuses: [...new Set(rows.map((row) => statusOptions?.[row[statusAccessor]] || row[statusAccessor]).filter(Boolean))].join(", ") })}
      triggerExport={triggerExport} setTriggerExport={setTriggerExport} resetTrigger={resetTrigger}
      pageResetTrigger={JSON.stringify([status, dateFilters.startDate, dateFilters.endDate, showExtra, filteredData.length])}
      getCellProps={(cell) => ({ className: cell.column.id === "icons" ? cell.column.actionClassName || "table-icons" : "" })} /></div>
  </section>;
}
